import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Flex, Grid, Input, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import type { IconType } from 'react-icons'
import { LuSettings, LuSave, LuHourglass, LuLock, LuActivity, LuCoins, LuLeaf, LuUndo2, LuCheck } from 'react-icons/lu'
import { analyticsService } from '../../../api/services/analytics.service'
import type { BottleneckConfig } from '../../../api/models/response/bottleneck'
import type { LaborStabilityConfig } from '../../../api/models/response/laborStability'
import type { CashProjectionConfig } from '../../../api/models/response/cashProjection'
import type { SustainabilityConfig } from '../../../api/models/response/sustainability'
import { useHasPermission } from '../../../auth/PermissionContext'
import {
  ReportPage,
  ReportHeader,
  ActionButton,
  Panel,
  ToneBadge,
  Notice,
} from '../../../components/analytics/ReportKit'
import { reportAccent, selectStyle } from '../../../components/analytics/reportTheme'
import { fonts, mineral } from '../../../theme'

// Pantalla central de configuración de los reportes analíticos. Separa "configurar" de
// "ver": aquí se editan los parámetros de cada reporte, gateado por el permiso .configure
// de cada uno; en el reporte solo queda la lectura de los "parámetros vigentes".
//
// Cada sección se guarda por separado (su propio endpoint). Para no tener cuatro botones
// terracota compitiendo, «Guardar» solo se vuelve la acción principal cuando la sección
// tiene cambios sin guardar; mientras tanto queda deshabilitado.

/* ─────────────────────────── Definición de campos ─────────────────────────── */

type Field<T> = {
  key: keyof T
  label: string
  /** Qué hace el parámetro y qué efecto tiene subirlo/bajarlo, en lenguaje llano. */
  help: string
  /** Unidad que se muestra junto al input. */
  unit?: string
  step?: number
  integer?: boolean
  /** Multiplicador para mostrar/editar (ej. fracción↔%: scale 100). */
  scale?: number
  /** Rango válido (en la unidad mostrada). Se valida antes de enviar. */
  min?: number
  max?: number
  /** Campo opcional: vacío se envía como null. */
  optional?: boolean
  emptyLabel?: string
  /** Lista de opciones: se edita con un <select>. */
  options?: { value: number; label: string }[]
}

type SectionDef<T> = {
  id: string
  icon: IconType
  title: string
  subtitle: string
  note?: string
  permission: string
  load: () => Promise<T>
  save: (v: T) => Promise<T>
  fields: Field<T>[]
}

/* ════ Cuellos de Botella ════ */
const BOTTLENECK: SectionDef<BottleneckConfig> = {
  id: 'cuellos',
  icon: LuHourglass,
  title: 'Cuellos de Botella',
  subtitle: 'Tolerancia límite por tramo (TL_k): horas que un lote puede pasar en cada etapa antes de marcarse como «dormido».',
  permission: 'analytics.bottleneck.configure',
  load: () => analyticsService.getBottleneckConfig(),
  save: (v) => analyticsService.updateBottleneckConfig(v),
  fields: [
    { key: 'transitoHours', label: 'Tránsito', unit: 'h', min: 0, help: 'De la salida de la mina a la llegada a recepción. Súbelo si las rutas son largas o con paradas habituales.' },
    { key: 'pesajeBrutoHours', label: 'Pesaje bruto', unit: 'h', min: 0, help: 'De recepción al pesaje bruto en balanza.' },
    { key: 'laboratorioHours', label: 'Laboratorio', unit: 'h', min: 0, help: 'Del pesaje bruto al resultado de laboratorio (muestreo y ensayo).' },
    { key: 'conciliacionHours', label: 'Conciliación', unit: 'h', min: 0, help: 'Del resultado de laboratorio a la conciliación de leyes con el proveedor.' },
    { key: 'pesajeHours', label: 'Pesaje neto', unit: 'h', min: 0, help: 'De la conciliación al peso neto definitivo.' },
    { key: 'liquidacionHours', label: 'Liquidación', unit: 'h', min: 0, help: 'Del peso neto al pago. Un valor bajo alerta antes de demoras en la liquidación.' },
  ],
}

/* ════ Estabilidad de la Labor ════ */
const LABOR: SectionDef<LaborStabilityConfig> = {
  id: 'estabilidad',
  icon: LuActivity,
  title: 'Estabilidad de la Labor',
  subtitle: 'Umbrales geológicos para detectar blanqueo: leyes demasiado parejas (CV) o saltos imposibles (Z).',
  permission: 'analytics.laborStability.configure',
  load: () => analyticsService.getLaborStabilityConfig(),
  save: (v) => analyticsService.updateLaborStabilityConfig(v),
  fields: [
    { key: 'cvMinPercent', label: 'CV mínimo (θ_min)', unit: '%', step: 0.1, min: 0, help: 'Variación natural mínima esperada en la ley de una labor. Por debajo, la serie es sospechosamente homogénea.' },
    { key: 'cvCriticalPercent', label: 'CV crítico (θ_crit)', unit: '%', step: 0.1, min: 0, help: 'Umbral de homogeneidad que ya se considera crítico (alerta roja).' },
    { key: 'zThreshold', label: 'Umbral Z', unit: 'σ', step: 0.1, min: 0, help: 'Cuántas desviaciones puede alejarse la ley de un lote de su historial antes de marcarse como salto geológico. Más bajo = más alertas.' },
    { key: 'bandSigmaK', label: 'Ancho de bandas (k)', unit: 'σ', step: 0.1, min: 0, help: 'Bandas de control del gráfico: media ± k·σ.' },
    { key: 'windowSize', label: 'Ventana', unit: 'lotes', integer: true, min: 1, help: 'Lotes recientes con los que se calcula la media y la desviación.' },
  ],
}

/* ════ Proyección de Caja ════ */
const CASH: SectionDef<CashProjectionConfig> = {
  id: 'caja',
  icon: LuCoins,
  title: 'Proyección de Caja',
  subtitle: 'Valorización predictiva: castigo prudencial y tiempos hasta que un lote se vuelve pagable.',
  permission: 'analytics.cashFlow.configure',
  load: () => analyticsService.getCashProjectionConfig(),
  save: (v) => analyticsService.updateCashProjectionConfig(v),
  fields: [
    // El factor de castigo se guarda como fracción [0..1] pero se edita como %.
    { key: 'cashPenaltyFactorBase', label: 'Factor de castigo', unit: '%', step: 0.1, scale: 100, min: 0, max: 100, help: 'Descuento prudencial sobre el valor estimado de los lotes aún sin ley de laboratorio.' },
    { key: 'leadTimeBalanzaHoursMin', label: 'Balanza — mínimo', unit: 'h', integer: true, min: 0, help: 'Tiempo más corto esperado entre la recepción y el pesaje en balanza.' },
    { key: 'leadTimeBalanzaHoursMax', label: 'Balanza — máximo', unit: 'h', integer: true, min: 0, help: 'Tiempo más largo esperado hasta la balanza. Con el mínimo forma la ventana de proyección.' },
    { key: 'leadTimeLabHours', label: 'Laboratorio', unit: 'h', integer: true, min: 0, help: 'Horas desde la balanza hasta tener la ley de laboratorio.' },
    { key: 'estimatedGradeHistoryMonths', label: 'Historial de ley', unit: 'meses', integer: true, min: 1, help: 'Meses de historial del proveedor para estimar la ley de los lotes que aún no tienen análisis.' },
  ],
}

/* ════ Huella de Carbono ════ */
const ROUTE_OPTIONS = [
  { value: 1, label: 'Plana / asfaltada' },
  { value: 2, label: 'Mixta / interprovincial' },
  { value: 3, label: 'Trocha / alta montaña' },
]

const SUSTAINABILITY: SectionDef<SustainabilityConfig> = {
  id: 'huella',
  icon: LuLeaf,
  title: 'Huella de Carbono',
  subtitle: 'Valores de respaldo del cálculo de emisiones cuando al viaje o al vehículo le falta un dato.',
  note: 'Los cambios aplican a los cálculos nuevos. Para los lotes ya calculados, usa «Recalcular» en Sostenibilidad.',
  permission: 'analytics.sustainability.configure',
  load: () => analyticsService.getSustainabilityConfig(),
  save: (v) => analyticsService.updateSustainabilityConfig(v),
  fields: [
    { key: 'defaultPerformanceKmPerGal', label: 'Rendimiento por defecto', unit: 'km/gal', step: 0.5, min: 0.1, max: 100, optional: true, emptyLabel: 'Sin valor',
      help: 'Se usa si el vehículo no tiene rendimiento registrado. Vacío: esos lotes quedan sin huella. Un camión minero típico rinde 6–10 km/gal.' },
    { key: 'minGpsPoints', label: 'GPS mínimo', unit: 'puntos', integer: true, min: 2, help: 'Puntos GPS necesarios para usar el trayecto real. Con menos (o si el trayecto no cubre la distancia mina→planta) se usa la ruta estimada.' },
    { key: 'defaultRouteType', label: 'Ruta por defecto', options: ROUTE_OPTIONS, help: 'Tipo de camino que se asume al estimar la distancia sin GPS. Elige el factor de tortuosidad que se aplica.' },
    { key: 'tortuosityPlana', label: 'Tortuosidad plana', unit: '×', step: 0.05, min: 1, max: 5, help: 'Multiplica la distancia en línea recta mina→planta en rutas planas o asfaltadas.' },
    { key: 'tortuosityMixta', label: 'Tortuosidad mixta', unit: '×', step: 0.05, min: 1, max: 5, help: 'Igual, para rutas mixtas o interprovinciales.' },
    { key: 'tortuosityTrocha', label: 'Tortuosidad trocha', unit: '×', step: 0.05, min: 1, max: 5, help: 'Igual, para trochas mineras o alta montaña (más curvas, factor mayor).' },
  ],
}

/* ─────────────────────────── Página ─────────────────────────── */

export default function ConfiguracionAnaliticaPage() {
  const sections = [
    { id: BOTTLENECK.id, icon: BOTTLENECK.icon, title: BOTTLENECK.title },
    { id: LABOR.id, icon: LABOR.icon, title: LABOR.title },
    { id: CASH.id, icon: CASH.icon, title: CASH.title },
    { id: SUSTAINABILITY.id, icon: SUSTAINABILITY.icon, title: SUSTAINABILITY.title },
  ]
  return (
    <ReportPage>
      <ReportHeader
        icon={LuSettings}
        title="Configuración de Analítica"
        subtitle="Parámetros de los reportes en un solo lugar. Cada sección se guarda por separado y solo es editable con el permiso de configuración de ese reporte."
      />

      {/* Índice de secciones */}
      <Flex gap={2} wrap="wrap">
        {sections.map((s, i) => (
          <motion.a key={s.id} href={`#cfg-${s.id}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * i }}
            onClick={(e) => {
              e.preventDefault()
              document.getElementById(`cfg-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}>
            <Flex align="center" gap={2} h="34px" px={3} borderRadius="999px" bg={mineral.surface} border={`1px solid ${mineral.line}`}
              fontSize="13px" fontWeight={600} color={mineral.ink2} transition="all .15s"
              _hover={{ borderColor: reportAccent.color, color: reportAccent.deep }}>
              <Box color={reportAccent.color} display="flex"><s.icon size={14} /></Box>
              {s.title}
            </Flex>
          </motion.a>
        ))}
      </Flex>

      <ConfigSection def={BOTTLENECK} index={0} />
      <ConfigSection def={LABOR} index={1} />
      <ConfigSection def={CASH} index={2} />
      <ConfigSection def={SUSTAINABILITY} index={3} />
    </ReportPage>
  )
}

/* ─────────────────────────── Sección genérica ─────────────────────────── */

// Valor de un campo → texto del input (en la unidad mostrada).
function toInput<T>(f: Field<T>, raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return ''
  const n = Number(raw)
  if (!Number.isFinite(n)) return ''
  const shown = f.scale ? n * f.scale : n
  return String(Math.round(shown * 1000) / 1000)
}

// Texto del input → número en la unidad del backend, o un error legible.
function parseField<T>(f: Field<T>, text: string): { value: number | null; error?: string } {
  const t = text.trim().replace(',', '.')
  if (t === '') {
    return f.optional ? { value: null } : { value: null, error: 'Obligatorio' }
  }
  const n = f.integer ? Number.parseInt(t, 10) : Number.parseFloat(t)
  if (!Number.isFinite(n)) return { value: null, error: 'Número inválido' }
  if (f.integer && !Number.isInteger(Number(t))) return { value: null, error: 'Debe ser entero' }
  if (f.min != null && n < f.min) return { value: null, error: `Mínimo ${f.min.toLocaleString('es-PE')}` }
  if (f.max != null && n > f.max) return { value: null, error: `Máximo ${f.max.toLocaleString('es-PE')}` }
  return { value: f.scale ? n / f.scale : n }
}

function display<T>(f: Field<T>, raw: unknown): string {
  if (f.options) return f.options.find((o) => o.value === Number(raw))?.label ?? '—'
  const t = toInput(f, raw)
  if (t === '') return f.emptyLabel ?? '—'
  const n = Number(t)
  return `${n.toLocaleString('es-PE', { maximumFractionDigits: 3 })}${f.unit ? ` ${f.unit}` : ''}`
}

function ConfigSection<T>({ def, index }: { def: SectionDef<T>; index: number }) {
  const canEdit = useHasPermission(def.permission)
  const [cfg, setCfg] = useState<T | null>(null)
  const [vals, setVals] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const { load, fields } = def

  const hydrate = useCallback((c: T) => {
    setCfg(c)
    setVals(Object.fromEntries(fields.map((f) => [String(f.key), toInput(f, c[f.key])])))
  }, [fields])

  const fetchCfg = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      hydrate(await load())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [load, hydrate])

  useEffect(() => { void fetchCfg() }, [fetchCfg])

  // El aviso «Guardado» se retira solo.
  useEffect(() => {
    if (savedAt == null) return
    const id = window.setTimeout(() => setSavedAt(null), 3500)
    return () => window.clearTimeout(id)
  }, [savedAt])

  const errors = useMemo(() => {
    const out: Record<string, string> = {}
    for (const f of fields) {
      if (f.options) continue
      const r = parseField(f, vals[String(f.key)] ?? '')
      if (r.error) out[String(f.key)] = r.error
    }
    return out
  }, [fields, vals])

  const dirty = useMemo(() => {
    if (!cfg) return false
    return fields.some((f) => (vals[String(f.key)] ?? '') !== toInput(f, cfg[f.key]))
  }, [cfg, fields, vals])

  const hasErrors = Object.keys(errors).length > 0

  const save = async () => {
    if (!cfg || hasErrors) return
    setSaving(true)
    setError(null)
    try {
      // Se envía la config completa (mismo contrato que antes): lo leído + lo editado.
      const out: T = { ...cfg }
      for (const f of fields) {
        const text = vals[String(f.key)] ?? ''
        const value = f.options ? Number(text) : parseField(f, text).value
        out[f.key] = value as T[keyof T]
      }
      hydrate(await def.save(out))
      setSavedAt(Date.now())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const discard = () => {
    if (cfg) hydrate(cfg)
    setError(null)
  }

  const status = !canEdit
    ? <ToneBadge tone="neutral"><LuLock size={10} /> Solo lectura</ToneBadge>
    : (
      <AnimatePresence mode="wait" initial={false}>
        {savedAt != null ? (
          <motion.span key="ok" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
            <ToneBadge tone="ok"><LuCheck size={10} /> Guardado</ToneBadge>
          </motion.span>
        ) : dirty ? (
          <motion.span key="dirty" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
            <ToneBadge tone="warn">Cambios sin guardar</ToneBadge>
          </motion.span>
        ) : null}
      </AnimatePresence>
    )

  return (
    <Box id={`cfg-${def.id}`} style={{ scrollMarginTop: 16 }}>
      <Panel index={index} padded={false}
        title={<Flex align="center" gap={2}><Box color={reportAccent.color} display="flex"><def.icon size={17} /></Box>{def.title}</Flex>}
        subtitle={def.subtitle}
        actions={status}>
        {loading && !cfg && (
          <Flex direction="column" gap={2} px={5} py={4}>
            {fields.map((f) => <Box key={String(f.key)} h="44px" borderRadius="10px" bg={mineral.sunk} opacity={0.7} />)}
          </Flex>
        )}

        {error && <Box px={5} pt={4}><Notice tone="bad">{error}</Notice></Box>}

        {cfg && (
          <Box>
            {fields.map((f, i) => (
              <FieldRow key={String(f.key)} field={f} first={i === 0} canEdit={canEdit}
                value={vals[String(f.key)] ?? ''} current={cfg[f.key]} error={errors[String(f.key)]}
                onChange={(v) => setVals((prev) => ({ ...prev, [String(f.key)]: v }))} />
            ))}

            {(def.note || canEdit) && (
              <Flex align={{ base: 'stretch', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3}
                px={5} py={3.5} borderTop={`1px solid ${mineral.line}`} bg={mineral.surfaceAlt}>
                <Text flex={1} fontSize="12px" color={mineral.ink3}>{def.note ?? ''}</Text>
                {canEdit && (
                  <Flex gap={2} justify="flex-end">
                    <ActionButton icon={LuUndo2} label="Descartar" variant="ghost" disabled={!dirty || saving} onClick={discard} />
                    <ActionButton icon={LuSave} label="Guardar cambios" variant={dirty && !hasErrors ? 'primary' : 'secondary'}
                      loading={saving} disabled={!dirty || hasErrors}
                      title={hasErrors ? 'Corrige los valores marcados' : undefined}
                      onClick={() => void save()} />
                  </Flex>
                )}
              </Flex>
            )}
          </Box>
        )}
      </Panel>
    </Box>
  )
}

/* ─────────────────────────── Fila de parámetro ─────────────────────────── */

function FieldRow<T>({ field: f, value, current, error, canEdit, first, onChange }: {
  field: Field<T>; value: string; current: unknown; error?: string; canEdit: boolean; first: boolean
  onChange: (v: string) => void
}) {
  const changed = canEdit && value !== toInput(f, current)
  return (
    <Grid templateColumns={{ base: '1fr', md: 'minmax(0, 1fr) 240px' }} gap={{ base: 2, md: 6 }} alignItems="center"
      px={5} py={3.5} borderTop={first ? `1px solid ${mineral.line}` : `1px dashed ${mineral.line}`}
      position="relative">
      {/* Marca lateral de campo modificado */}
      <AnimatePresence>
        {changed && (
          <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} exit={{ scaleY: 0 }} transition={{ duration: 0.18 }}
            style={{ position: 'absolute', left: 0, top: 10, bottom: 10, width: 3, borderRadius: 2, background: mineral.oro }} />
        )}
      </AnimatePresence>
      <Box minW={0}>
        <Text fontSize="14px" fontWeight={600} color={mineral.ink}>{f.label}</Text>
        <Text fontSize="12.5px" color={mineral.ink3} mt={0.5} lineHeight="1.5">{f.help}</Text>
      </Box>

      <Box>
        {!canEdit ? (
          <Text fontSize="14px" fontWeight={700} color={mineral.ink} textAlign={{ base: 'left', md: 'right' }}>{display(f, current)}</Text>
        ) : f.options ? (
          <select style={selectStyle} value={value} onChange={(e) => onChange(e.target.value)} aria-label={f.label}>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <Box>
            <Flex align="center" h="40px" borderRadius="10px" overflow="hidden" bg="#fff"
              border={`1px solid ${error ? mineral.aji : changed ? mineral.oro : mineral.lineStrong}`}
              transition="border-color .15s" _focusWithin={{ borderColor: error ? mineral.aji : reportAccent.color }}>
              <Input flex={1} minW={0} h="38px" border="none" px={3} fontSize="13.5px" textAlign="right"
                type="number" inputMode="decimal" step={f.integer ? 1 : f.step}
                placeholder={f.optional ? (f.emptyLabel ?? 'Sin valor') : undefined}
                value={value} onChange={(e) => onChange(e.target.value)} aria-label={f.label}
                _focusVisible={{ outline: 'none', boxShadow: 'none' }} />
              {f.unit && (
                <Flex align="center" h="100%" px={3} bg={mineral.surfaceAlt} borderLeft={`1px solid ${mineral.line}`}
                  fontSize="12px" color={mineral.ink3} fontFamily={fonts.mono} minW="64px" justify="center">
                  {f.unit}
                </Flex>
              )}
            </Flex>
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Text fontSize="11.5px" color={mineral.aji} mt={1} textAlign="right">{error}</Text>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        )}
      </Box>
    </Grid>
  )
}
